import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import './styles.css';
import { API_BASE_URL as API_URL } from '../../config/apiBase';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);


const AutomobilesPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/chart/automobile`);
      if (!res.ok) throw new Error("Failed to fetch automobile data");
      const json = await res.json();
      setData(json);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const barChartData = useMemo(() => {
    const labels = (data.barData || []).map(item => item.grade);
    return {
      labels,
      datasets: [
        {
          label: "Female",
          data: (data.barData || []).map(item => item.female),
          backgroundColor: "rgba(255, 99, 132, 0.7)"
        },
        {
          label: "Male",
          data: (data.barData || []).map(item => item.male),
          backgroundColor: "rgba(54, 162, 235, 0.7)"
        }
      ]
    };
  }, [data]);
  

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      title: { display: false },
      tooltip: {
        callbacks: {
          label: ctx => `${(ctx.parsed.y || 0).toLocaleString()}`
        }
      }
    },
    scales: {
      x: { grid: { color: 'rgba(102,126,234,0.1)', drawBorder: false } },
      y: { beginAtZero: true, grid: { color: 'rgba(102,126,234,0.1)', drawBorder: false } },
    },
    interaction: { intersect: false, mode: 'index' }
  };

  const doughnutData = useMemo(() => {
    const labels = (data.donutData || []).map(item => item.status);
    const values = (data.donutData || []).map(item => item.count);
    return {
      labels,
      datasets: [
        {
          data: values,
          backgroundColor: ["rgba(102,126,234,0.8)", "rgba(240,147,251,0.8)"],
          borderColor: ["#667eea", "#f093fb"],
          borderWidth: 3
        }
      ]
    };
  }, [data]);
  

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '60%',
    plugins: {
      legend: { position: 'bottom' },
      title: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const val = ctx.parsed || 0;
            const total = (ctx.dataset.data || []).reduce((a, b) => a + b, 0) || 1;
            const pct = ((val / total) * 100).toFixed(1);
            return `${ctx.label}: ${val.toLocaleString()} (${pct}%)`;
          }
        }
      }
    },
    elements: { arc: { borderWidth: 3 } },
    animation: { animateRotate: true, animateScale: true, duration: 1200 }
  };

  if (loading) {
    return (
      <div className="auto-page">
        <div className="auto-container">
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading automobile data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="auto-page">
        <div className="auto-container">
          <div className="error-state">
            <h3>Error Loading Data</h3>
            <p>{error}</p>
            <button onClick={fetchData} className="retry-button">Try Again</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auto-page">
      <div className="auto-container">
        <header className="sector-header">
          <h1>Automobile Sector</h1>
          <button 
            className="back-to-dashboard-btn"
            onClick={() => navigate('/dashboard')}
          >
            Back to Dashboard
          </button>
        </header>
        <div className="auto-grid">
          <div className="auto-card">
            <h2 className="auto-title">Automobile - 2D Columns</h2>
            <div className="auto-chart">
              <Bar data={barChartData} options={barOptions} />
            </div>
          </div>
          <div className="auto-card">
            <h2 className="auto-title">Automobile - Donut</h2>
            <div className="auto-chart">
              <Doughnut data={doughnutData} options={doughnutOptions} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AutomobilesPage;


